/**
 * React bindings for the pinned client.
 *
 * Three responsibilities, and no endpoint knowledge whatsoever:
 *
 *   1  put the planner's locale on the request, so the service's RFC 9457 `title` and
 *      `detail` come back in it (G6, G12);
 *   2  make the locale part of the cache key, so switching language does not serve a
 *      cached Italian error to an English screen;
 *   3  turn a thrown `ApiProblemError` into a message the i18n runtime formats — never
 *      into raw server prose on a planner's screen.
 *
 * Retry policy is inherited from the shared `QueryClient` in `src/app/App.tsx` (queries
 * retry twice, mutations not at all — G2 §2.6: "NEVER a silent discard of a planner's
 * edit"), except that a 4xx is not retried here: a request the service rejected as
 * invalid, unauthorised or not found will be rejected identically three times, and the
 * only thing the retries buy is three times the wait before the planner is told.
 */

import { useMutation, useQueries, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { OperationDescriptor, ParamsOf, ResultOf } from './generated/operations';
import {
  callOperation, collectPages, newIdempotencyKey,
  type ApiRequestOptions, type ItemOf,
} from './http';
import { BACKGROUND_READ } from '../primitives/read-activity';
import { ApiProblemError, isPermissionProblem, problemMessage } from './problem';
import { collectionKey } from './session-cache';

export { SESSION_LONG_READ } from './session-cache';
export { BACKGROUND_READ };

/**
 * The retry rule of every read, and the shell's default (`src/app/App.tsx`): a failure another
 * attempt cannot fix is not retried. A 4xx problem document is the service's answer — a designed
 * «not published / not frozen / not found» (`programme.report_not_frozen`, a 404) will be given
 * identically three times, and the retries buy only the wait before the planner is told. A
 * network failure, a 5xx or an answer with no problem document keeps its two retries.
 */
export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiProblemError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

export interface UseApiQueryOptions {
  /** Skip the request — for a query whose parameters are not known yet. */
  readonly enabled?: boolean;
  readonly baseUrl?: string;
  /**
   * How long the answer counts as fresh. The app's default (`App.tsx`: 30 s) suits a read that
   * moves; a read that is a property of an IMMUTABLE key — a run's report — names `Infinity`,
   * per call, so the page that comes back to it paints from the cache and asks nothing.
   */
  readonly staleTime?: number;
  /** How long an unused answer is kept (the app's default is 5 minutes). */
  readonly gcTime?: number;
  /**
   * The query's `meta`. A read nobody waits for spreads `BACKGROUND_READ` into its options, which
   * keeps it out of the foreground count a button's busy state and a placeholder's `aria-busy` ask
   * (the shell's progress line still counts it).
   */
  readonly meta?: typeof BACKGROUND_READ.meta;
}

/** The React Query lifetime and meta options a caller named; nothing for one that named none. */
function lifetimeOf(options: UseApiQueryOptions): { staleTime?: number; gcTime?: number; meta?: typeof BACKGROUND_READ.meta } {
  return {
    ...(options.meta === undefined ? {} : { meta: options.meta }),
    ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
    ...(options.gcTime === undefined ? {} : { gcTime: options.gcTime }),
  };
}

/**
 * One operation, one query.
 *
 * `params` is typed by the operation, so the wrong shape is a compile error rather than
 * a 422 discovered by a planner.
 */
export function useApiQuery<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: UseApiQueryOptions = {},
): UseQueryResult<ResultOf<S>, Error> {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const { enabled, baseUrl } = options;

  return useQuery<ResultOf<S>, Error>({
    queryKey: [operation.operationId, params, language],
    queryFn: ({ signal }) => {
      const request: ApiRequestOptions = {
        language,
        signal,
        // The screen's own read: never counted as a download's pages (`./walk-progress.ts`).
        untracked: true,
        ...(baseUrl === undefined ? {} : { baseUrl }),
      };
      return callOperation(operation, params, request);
    },
    retry: shouldRetryRead,
    ...lifetimeOf(options),
    ...(enabled === undefined ? {} : { enabled }),
  });
}

/**
 * One operation, many questions of it — a list whose length the caller decides.
 *
 * **Why this is not a loop over `useApiQuery`.** React identifies a hook by its
 * call order, so a screen that called `useApiQuery` once per selected option
 * would break the moment the planner selected a different number of them. The
 * `useQueries` binding exists for exactly this shape: one hook call, a list of
 * query descriptors inside it.
 *
 * **Why it is not a batch endpoint either.** There isn't one. `/markdown/path`
 * answers for a single option × cluster × channel, so "solve these two hundred"
 * is two hundred requests however it is spelled. Putting the fan-out here rather
 * than in a screen keeps the rule `src/api/` exists for: a screen still never
 * builds a URL, names a header, or decides a retry policy. Each question keeps
 * its own cache entry, so re-asking about one option does not re-ask about the
 * other hundred and ninety-nine.
 *
 * Every element resolves independently: a refusal on one is that element's
 * `error`, not the list's. That is the behaviour the markdown results table
 * needs, because most of its rows are refusals and each names a different
 * missing feed.
 */
export function useApiQueries<S extends OperationDescriptor>(
  operation: S,
  paramsList: readonly ParamsOf<S>[],
  options: UseApiQueryOptions = {},
): UseQueryResult<ResultOf<S>, Error>[] {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const { enabled, baseUrl } = options;

  return useQueries({
    queries: paramsList.map((params) => ({
      queryKey: [operation.operationId, params, language],
      queryFn: ({ signal }: { signal: AbortSignal }) => {
        const request: ApiRequestOptions = {
          language,
          signal,
          ...(baseUrl === undefined ? {} : { baseUrl }),
        };
        return callOperation(operation, params, request);
      },
      retry: shouldRetryRead,
      ...lifetimeOf(options),
      ...(enabled === undefined ? {} : { enabled }),
    })),
  }) as UseQueryResult<ResultOf<S>, Error>[];
}

/**
 * One collection, every page.
 *
 * The cursor is followed inside the query function rather than by the caller, so a screen
 * never renders a first page and calls it the answer. `collectPages` refuses to walk past
 * `MAX_PAGES`, so an unbounded perimeter fails loudly instead of hanging.
 */
export function useApiCollection<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: UseApiQueryOptions = {},
): UseQueryResult<readonly ItemOf<S>[], Error> {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const { enabled, baseUrl } = options;

  return useQuery<readonly ItemOf<S>[], Error>({
    queryKey: collectionKey(operation, params, language),
    queryFn: ({ signal }) => {
      const request: ApiRequestOptions = {
        language,
        signal,
        ...(baseUrl === undefined ? {} : { baseUrl }),
      };
      return collectPages(operation, params, request) as Promise<readonly ItemOf<S>[]>;
    },
    retry: shouldRetryRead,
    ...lifetimeOf(options),
    ...(enabled === undefined ? {} : { enabled }),
  });
}

// ── writes ───────────────────────────────────────────────────────────────────

export interface UseApiMutationOptions {
  readonly baseUrl?: string;
  /**
   * An explicit key for this decision, where the caller already has a stable identifier
   * for it — a draft id, a proposal set hash. Omit it and one is minted; see below for
   * when.
   */
  readonly idempotencyKey?: string;
}

/**
 * One operation, one write.
 *
 * `12` §2.4 control 2 forbids a hand-rolled request. Until this existed, `src/api/`
 * bound only reads, and the two chapters that met the gap drew opposite conclusions from
 * it: ch03 removed a working button rather than hand-roll a `fetch`, which was the right
 * reading of the control; ch05 went through `callOperation` and the generated descriptors
 * with React Query's own `useMutation`, which is not hand-rolling and was legitimate. One
 * of them lost a control to a missing binding. This is that binding, and it follows ch05.
 *
 * ── The idempotency key belongs to the DECISION, not to the request ─────────────────
 *
 * `http.ts` mints a key per call when the caller supplies none. That is right for a write
 * a caller never repeats and wrong for every bulk action: a planner who double-clicks
 * Approve, or whose first attempt died at the network and who presses it again, means ONE
 * decision — and two minted keys would record two bulk actions over the same forty
 * proposals. G12 puts the header on the write; it does not say when the key is born.
 *
 * So the key is minted when the DECISION IS COMPOSED — which is what `params` is: the
 * fully-specified action, held in the calling component's state. It is
 *
 *   * minted on the first attempt for a given `params`,
 *   * HELD across every retry of that same action, so a repeat is deduplicated by the
 *     service (`platform.idempotency_key_reused`) rather than applied twice,
 *   * DISCARDED when `params` change, because different parameters are a different
 *     decision and reusing the key would have the service refuse the new one,
 *   * DISCARDED on success, so composing the same action again is a new decision — a
 *     planner who deliberately approves the same set twice has decided twice.
 *
 * `params` is compared by VALUE rather than by identity: a component that rebuilds its
 * parameter object every render has not changed its mind.
 *
 * `mutate()` therefore takes no variables. That is the point rather than an omission: the
 * action must be fully composed before it can be fired, which is what makes "the key
 * identifies the decision" true rather than aspirational.
 */
export function useApiMutation<S extends OperationDescriptor>(
  operation: S,
  params: ParamsOf<S>,
  options: UseApiMutationOptions = {},
): UseMutationResult<ResultOf<S>, Error, void> {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const { baseUrl, idempotencyKey } = options;

  const signature = JSON.stringify(params ?? null);
  const held = useRef<{ signature: string; key: string } | null>(null);

  const mutation = useMutation<ResultOf<S>, Error, void>({
    mutationKey: [operation.operationId, signature],
    mutationFn: () => {
      if (held.current === null || held.current.signature !== signature) {
        held.current = { signature, key: idempotencyKey ?? newIdempotencyKey() };
      }
      const request: ApiRequestOptions = {
        language,
        idempotencyKey: held.current.key,
        ...(baseUrl === undefined ? {} : { baseUrl }),
      };
      return callOperation(operation, params, request);
    },
    onSuccess: () => { held.current = null; },
  });

  // Checked AFTER every hook, so the hook order is the same on every render whatever the
  // descriptor is. A GET has no idempotency key, no write semantics and no entry in the
  // query cache this hook does not populate — binding one here would be a read that never
  // revalidates, which is a bug that presents as stale numbers rather than as an error.
  if (operation.method === 'GET') {
    throw new Error(
      `Operation ${operation.operationId} is a GET. useApiMutation binds writes; a read goes through useApiQuery or useApiCollection.`,
    );
  }

  return mutation;
}

/**
 * One operation, fired with its parameters at the moment of the decision — the imperative twin
 * of {@link useApiMutation}, for a control that learns WHAT is decided only when it is decided:
 * the approval queues of `docs/adr/9032`, where the row or the group and its reason arrive with
 * the key press.
 *
 * The idempotency key follows the same rule, held per decision rather than per request: minted
 * on the first attempt for a given `params` (compared by value), HELD across every retry of that
 * same decision — so a double press, or a press again after the network died, is one decision —
 * and DISCARDED when it succeeds, so deciding the same thing again later is a new decision. A
 * different `params` is a different decision with its own key.
 */
export function useApiCommand<S extends OperationDescriptor>(
  operation: S,
  options: Omit<UseApiMutationOptions, 'idempotencyKey'> = {},
): (params: ParamsOf<S>) => Promise<ResultOf<S>> {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const { baseUrl } = options;
  const held = useRef(new Map<string, string>());
  const command = useCallback(async (params: ParamsOf<S>): Promise<ResultOf<S>> => {
    const signature = JSON.stringify(params ?? null);
    const key = held.current.get(signature) ?? newIdempotencyKey();
    held.current.set(signature, key);
    const result = await callOperation(operation, params, {
      language,
      idempotencyKey: key,
      ...(baseUrl === undefined ? {} : { baseUrl }),
    });
    held.current.delete(signature);
    return result;
  }, [operation, language, baseUrl]);
  if (operation.method === 'GET') {
    throw new Error(
      `Operation ${operation.operationId} is a GET. useApiCommand binds writes; a read goes through useApiQuery or useApiCollection.`,
    );
  }
  return command;
}

/**
 * Formats a failure into a message.
 *
 * Every branch ends in `t(key, values)`. There is no path through this function that puts
 * an untranslated string on a screen, which is what G6 test a is checking for and what
 * `api.problem.unknown`'s `{detail}` placeholder exists to make possible without a
 * concatenation (G6 test b).
 */
export function useProblemMessage(): (error: unknown) => string {
  const { t } = useTranslation();
  return useCallback((error: unknown) => {
    const message = problemMessage(error);
    return t(message.key, message.values);
  }, [t]);
}

export { isPermissionProblem };
